import * as React from "react";

import {
  useCustomerCartId,
  useCustomerInformationQuery,
  useCustomerSession,
  useProminentError,
} from "../../../Common";

import { useMyBagAnalytics } from "../my-bag-analytics-provider";

import { CheckoutModel_MyBag } from "../../model";

import {
  DataMutationMyBagItemChangeQuantity_Request,
  useDataMutationMyBagItemChangeQuantity_asController,
} from "../data-mutation--mybag-item-change-quantity";
import {
  DataMutationMyBagItemRemove_Request,
  useDataMutationMyBagItemRemove_asController,
} from "../data-mutation--mybag-item-remove";
import {
  DataMutationMyBagPromoCodeApply_Request,
  useDataMutationMyBagPromoCodeApply_asController,
} from "../data-mutation--mybag-promo-code-apply";
import {
  DataMutationMyBagPromoCodeRemove_Request,
  useDataMutationMyBagPromoCodeRemove_asController,
} from "../data-mutation--mybag-promo-code-remove";
import {
  ReactCheckout2022Route,
  useReactCheckout2022Router_asController,
} from "../react-checkout-2022-router-provider";

import { useMyBagDataSourceIntersection } from "./my-bag-data-source-intersection-provider";
import {
  MyBagItemsRunningState_RequestChangeQuantity,
  MyBagItemsRunningState_UnregisterRequest,
  MyBagItemsRunningState_WorkingModificationsToQuantity_Status,
  useMyBagItemsRunningState_asController,
} from "./my-bag-items-running-state";
import { useMyBagOutOfStockUserExperience_asController } from "./my-bag-out-of-stock-user-experience-provider";
import { useMyBagPresentationModel } from "./my-bag-presentation-model-provider";

export interface MyBagUserExperienceConsumerInterface {
  canIProceedToCheckout: boolean;
}
const defaultValue = {} as unknown as MyBagUserExperienceConsumerInterface;
const MyBagUserExperienceContext = React.createContext(defaultValue);

export interface MyBagUserExperience_ControllerInterface {
  handleRemoveItem: (
    presentationMyBagItem: CheckoutModel_MyBag
  ) => Promise<void>;
  handleChangeItemQuantity: (
    presentationMyBagItem: CheckoutModel_MyBag,
    newQuantity: number
  ) => Promise<void>;
  handlePromoCodeApply: (code: string) => Promise<void>;
  handlePromoCodeRemove: () => Promise<void>;
  handleCheckout: () => Promise<void>;
}
const controllerDefaultValue =
  {} as unknown as MyBagUserExperience_ControllerInterface;
const MyBagUserExperience_ControllerContext = React.createContext(
  controllerDefaultValue
);

export enum MyBagUserExperience_ProminentErrorAreas {
  PROMO_CODE = "PROMO_CODE",
}

export const MyBagUserExperienceProvider: React.FunctionComponent = (props) => {
  const { cartId } = useCustomerCartId();

  const { setProminentError } = useProminentError();

  const {
    registerWorkingModificationToQuantity,
    registerStatusForWorkingModificationToQuantity,
    unregisterWorkingModificationToQuantity,
  } = useMyBagItemsRunningState_asController();
  const { mutateAsync: mutateItemRemove } =
    useDataMutationMyBagItemRemove_asController();
  const { mutateAsync: mutateItemChangeQuantity } =
    useDataMutationMyBagItemChangeQuantity_asController();
  const { mutateAsync: mutatePromoCodeApply } =
    useDataMutationMyBagPromoCodeApply_asController();
  const { mutateAsync: mutatePromoCodeRemove } =
    useDataMutationMyBagPromoCodeRemove_asController();

  const { handleNeedsToShowOutOfStockDrawer } =
    useMyBagOutOfStockUserExperience_asController();

  const { mostLatestType } = useMyBagDataSourceIntersection();
  const { presentationMyBagItems, presentationMyBagOrder } =
    useMyBagPresentationModel();

  const { navigateTo } = useReactCheckout2022Router_asController();

  const canIProceedToCheckout = React.useMemo(() => {
    if (mostLatestType !== "data" || !presentationMyBagOrder) {
      return false;
    }

    const areThereAnyItems = presentationMyBagItems.length > 0;

    const ifThereAreAnyExplicitlyOutOfStockItems =
      false; /*presentationMyBagItems.some(
      (presentationMyBagItem) =>
        presentationMyBagItem.currentStockStatus === "OUT_OF_STOCK"
    );*/

    const canIProceedToCheckout =
      areThereAnyItems && !ifThereAreAnyExplicitlyOutOfStockItems;

    return canIProceedToCheckout;
  }, [mostLatestType, presentationMyBagItems, presentationMyBagOrder]);

  const handleRemoveItem: MyBagUserExperience_ControllerInterface["handleRemoveItem"] =
    React.useCallback(
      async (presentationMyBagItem) => {
        if (!cartId) {
          return;
        }
        const request: DataMutationMyBagItemRemove_Request = {
          cartId,
          cartItemToRemoveId: presentationMyBagItem.id,
        };
        try {
          await mutateItemRemove(request);
        } catch (anyError) {
          console.error(anyError);
          return;
        }

        const unregisterWorkingModificationRequest: MyBagItemsRunningState_UnregisterRequest =
          {
            cartId,
            cartItemToUpdateId: presentationMyBagItem.id,
          };
        unregisterWorkingModificationToQuantity(
          unregisterWorkingModificationRequest
        );
      },
      [cartId, mutateItemRemove, unregisterWorkingModificationToQuantity]
    );

  const handleChangeItemQuantity: MyBagUserExperience_ControllerInterface["handleChangeItemQuantity"] =
    React.useCallback(
      async (presentationMyBagItem, newQuantity) => {
        if (!cartId) {
          return;
        }
        if (newQuantity <= 0) {
          return handleRemoveItem(presentationMyBagItem);
        }

        const updateWorkingTree: MyBagItemsRunningState_RequestChangeQuantity =
          {
            cartId,
            cartItemToUpdateId: presentationMyBagItem.id,
            quantity: newQuantity,
          };
        registerWorkingModificationToQuantity(updateWorkingTree);

        const request: DataMutationMyBagItemChangeQuantity_Request = {
          cartId,
          cartItemId_toChange: presentationMyBagItem.id,
          newQuantity,
        };
        try {
          await mutateItemChangeQuantity(request);
        } catch (anyError) {
          const OUT_STOCK_ERROR = "The requested qty is not available";
          const OUT_STOCK_UPDATE = "This product is out of stock";

          const thisError = anyError as Error;

          const isThisAnOutOfStockError =
            thisError.message.includes(OUT_STOCK_ERROR) ||
            thisError.message.includes(OUT_STOCK_UPDATE);
          if (isThisAnOutOfStockError) {
            registerStatusForWorkingModificationToQuantity(
              updateWorkingTree,
              MyBagItemsRunningState_WorkingModificationsToQuantity_Status.rejectedOutOfStock
            );
          } else {
            registerStatusForWorkingModificationToQuantity(
              updateWorkingTree,
              MyBagItemsRunningState_WorkingModificationsToQuantity_Status.rejected
            );
          }

          console.error(anyError);
          return;
        }

        registerStatusForWorkingModificationToQuantity(
          updateWorkingTree,
          MyBagItemsRunningState_WorkingModificationsToQuantity_Status.approved
        );
      },
      [
        cartId,
        registerWorkingModificationToQuantity,
        mutateItemChangeQuantity,
        registerStatusForWorkingModificationToQuantity,
        registerStatusForWorkingModificationToQuantity,
      ]
    );

  const handlePromoCodeApply: MyBagUserExperience_ControllerInterface["handlePromoCodeApply"] =
    React.useCallback(
      async (code) => {
        if (!code || code.trim().length < 1) {
          return;
        }
        if (!cartId) {
          return;
        }

        const request: DataMutationMyBagPromoCodeApply_Request = {
          cartId,
          couponCode: code,
        };
        try {
          await mutatePromoCodeApply(request);
        } catch (anyError) {
          console.error(anyError);
          if (anyError && (anyError as Error).message) {
            setProminentError(
              (anyError as Error).message,
              MyBagUserExperience_ProminentErrorAreas.PROMO_CODE
            );
          }
          return;
        }
      },
      [cartId, mutatePromoCodeApply]
    );

  const handlePromoCodeRemove: MyBagUserExperience_ControllerInterface["handlePromoCodeRemove"] =
    React.useCallback(async () => {
      if (!cartId) {
        return;
      }

      const request: DataMutationMyBagPromoCodeRemove_Request = {
        cartId,
      };
      try {
        await mutatePromoCodeRemove(request);
      } catch (anyError) {
        console.error(anyError);
        if (anyError && (anyError as Error).message) {
          setProminentError((anyError as Error).message);
        }
        return;
      }
    }, [cartId, mutatePromoCodeApply]);

  const { analytics } = useMyBagAnalytics();
  const { isCustomerLoggedIn } = useCustomerSession();
  const { data: customerInformationData } = useCustomerInformationQuery();

  const handleCheckout: MyBagUserExperience_ControllerInterface["handleCheckout"] =
    React.useCallback(async () => {
      if (!presentationMyBagOrder) {
        return;
      }

      const ifThereAreAnyUnavailableItems =
        presentationMyBagItems.some(
          (presentationMyBagItem) => presentationMyBagItem.isUnavailable
        );
      if (ifThereAreAnyUnavailableItems) {
        handleNeedsToShowOutOfStockDrawer();
        return;
      }

      // if (presentationMyBagOrder.isThisAVirtualOrder) {
      //   navigateTo(ReactCheckout2022Route.payment);
      // } else {
      //   navigateTo(ReactCheckout2022Route.delivery_details);
      // }

      if (presentationMyBagItems?.[0]?.sku) {
        const ga4Obj = {
          event: "begin_checkout",
          ecommerce: {
            currency: "AUD",
            value: parseFloat(
              document.querySelector(
                "#bbnt-new-checkout-2022--mybag > section > section.details > section.my-bag-summary > div.by-the-numbers > div.totalValueBreakdown > div.total-inc-gst > p.the-price.add-price > span.value"
              )?.innerText
            ),
            items: presentationMyBagItems.map((item) => ({
              item_id: item.sku,
              item_name: item.title,
              price: item.subtotal,
              quantity: item.howManyOfThisProduct,
            })),
          },
        };
        window.dataLayer = Array.isArray(window.dataLayer)
          ? window.dataLayer
          : [];
        window.dataLayer.push(ga4Obj);
      }

      // Triple Whale checkoutStarted: identity and lines come from the backend, a guest
      // without a captured e-mail is held until the e-mail is set on the cart.
      analytics.checkoutStarted({
        email: isCustomerLoggedIn ? customerInformationData?.email || null : null,
      });

      navigateTo(ReactCheckout2022Route.delivery_details);
    }, [
      presentationMyBagOrder,
      presentationMyBagItems,
      handleNeedsToShowOutOfStockDrawer,
      navigateTo,
      analytics,
      isCustomerLoggedIn,
      customerInformationData?.email,
    ]);

  const value: MyBagUserExperienceConsumerInterface = React.useMemo(
    () => ({ canIProceedToCheckout }),
    [canIProceedToCheckout]
  );
  const controllerValue: MyBagUserExperience_ControllerInterface =
    React.useMemo(
      () => ({
        handleRemoveItem,
        handleChangeItemQuantity,
        handlePromoCodeApply,
        handlePromoCodeRemove,
        handleCheckout,
      }),
      [
        handleRemoveItem,
        handleChangeItemQuantity,
        handlePromoCodeApply,
        handlePromoCodeRemove,
        handleCheckout,
      ]
    );
  return (
    <MyBagUserExperience_ControllerContext.Provider value={controllerValue}>
      <MyBagUserExperienceContext.Provider value={value}>
        {props.children || []}
      </MyBagUserExperienceContext.Provider>
    </MyBagUserExperience_ControllerContext.Provider>
  );
};

export const useMyBagUserExperience = () => {
  const value = React.useContext(MyBagUserExperienceContext);
  if (value === defaultValue) {
    throw new Error(
      "useMyBagUserExperience must be used within a MyBagUserExperienceProvider"
    );
  }
  return value;
};

export const useMyBagUserExperience_asController = () => {
  const value = React.useContext(MyBagUserExperience_ControllerContext);
  if (value === controllerDefaultValue) {
    throw new Error(
      "useMyBagUserExperience_asController must be used within a MyBagUserExperienceProvider"
    );
  }
  return value;
};
